/** 钱包未安装时的 Web 下载 / H5 Deep Link 引导 */

const MOBILE_UA_RE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i;

export function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  return MOBILE_UA_RE.test(navigator.userAgent || '');
}

function getCurrentPageUrl() {
  if (typeof window === 'undefined') return '';
  return window.location.href;
}

function toMetamaskDappPath(url) {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}${u.search}`;
  } catch {
    return String(url).replace(/^https?:\/\//, '');
  }
}

/** @type {Record<string, { downloadUrl: string, mobileDeepLink?: (pageUrl: string) => string | null, mobileFallbackUrl?: string }>} */
export const WALLET_INSTALL_CONFIG = {
  metamask: {
    downloadUrl: 'https://metamask.io/download/',
    mobileDeepLink: (pageUrl) => `https://metamask.app.link/dapp/${toMetamaskDappPath(pageUrl)}`,
    mobileFallbackUrl: 'https://metamask.io/download/',
  },
  coinbase: {
    downloadUrl: 'https://www.coinbase.com/wallet/downloads',
    mobileDeepLink: (pageUrl) => `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(pageUrl)}`,
    mobileFallbackUrl: 'https://www.coinbase.com/wallet/downloads',
  },
  okx: {
    downloadUrl: 'https://www.okx.com/download',
    mobileDeepLink: (pageUrl) => `okx://wallet/dapp/url?dappUrl=${encodeURIComponent(pageUrl)}`,
    mobileFallbackUrl: 'https://www.okx.com/download',
  },
  bitget: {
    downloadUrl: 'https://web3.bitget.com/wallet-download',
    mobileDeepLink: (pageUrl) =>
      `https://bkcode.vip?action=dapp&url=${encodeURIComponent(pageUrl)}`,
    mobileFallbackUrl: 'https://web3.bitget.com/wallet-download',
  },
  tronlink: {
    downloadUrl: 'https://www.tronlink.org/',
    mobileDeepLink: (pageUrl) => {
      const param = JSON.stringify({
        url: pageUrl,
        action: 'open',
        protocol: 'tronlink',
        version: '1.0',
      });
      return `tronlinkoutside://pull.activity?param=${encodeURIComponent(param)}`;
    },
    mobileFallbackUrl: 'https://www.tronlink.org/',
  },
  phantom: {
    downloadUrl: 'https://phantom.app/download',
    mobileDeepLink: (pageUrl) => `https://phantom.app/ul/browse/${encodeURIComponent(pageUrl)}`,
    mobileFallbackUrl: 'https://phantom.app/download',
  },
  walletconnect: {
    downloadUrl: 'https://walletconnect.com/',
    mobileFallbackUrl: 'https://walletconnect.com/',
  },
};

export function getWalletDownloadUrl(walletId) {
  const config = WALLET_INSTALL_CONFIG[walletId];
  return config?.downloadUrl || null;
}

export function getWalletMobileFallbackUrl(walletId) {
  const config = WALLET_INSTALL_CONFIG[walletId];
  return config?.mobileFallbackUrl || config?.downloadUrl || null;
}

export function getWalletMobileDeepLink(walletId, pageUrl = getCurrentPageUrl()) {
  const config = WALLET_INSTALL_CONFIG[walletId];
  if (!config?.mobileDeepLink) return null;
  return config.mobileDeepLink(pageUrl) || null;
}

export function getWalletConnectUniversalLink(uri) {
  return `https://walletconnect.com/wc?uri=${encodeURIComponent(uri)}`;
}

export function openWalletDownload(walletId) {
  const url = getWalletDownloadUrl(walletId);
  if (!url || typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * H5：尝试唤起钱包 App。
 * - 超时仍未离开页面 → onFallback（视为未安装 / 唤起失败）
 * - 已切入 App 后又回到浏览器 → onFallback（展示重试/下载）
 * @param {string} walletId
 * @param {{ pageUrl?: string, onFallback?: () => void, timeoutMs?: number }} [options]
 * @returns {() => void} cancel
 */
export function openWalletMobileApp(walletId, options = {}) {
  const { pageUrl = getCurrentPageUrl(), onFallback, timeoutMs = 2500 } = options;
  const link = getWalletMobileDeepLink(walletId, pageUrl);
  if (!link || typeof window === 'undefined') {
    onFallback?.();
    return () => {};
  }

  let settled = false;
  let leftForApp = false;
  let timer = null;

  const cleanup = () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    if (timer != null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  const finishFallback = () => {
    if (settled) return;
    settled = true;
    cleanup();
    onFallback?.();
  };

  const markLeftForApp = () => {
    leftForApp = true;
    if (timer != null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  const onVisibilityChange = () => {
    if (document.hidden) {
      markLeftForApp();
      return;
    }
    if (leftForApp) {
      finishFallback();
    }
  };

  const onPageHide = () => {
    markLeftForApp();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  window.location.href = link;

  timer = window.setTimeout(() => {
    timer = null;
    if (settled) return;
    if (!leftForApp && !document.hidden) {
      finishFallback();
      return;
    }
    // 已切入 App：保留监听，等用户回到浏览器再 onFallback
  }, timeoutMs);

  return () => {
    if (settled) return;
    settled = true;
    cleanup();
  };
}

/**
 * 未检测到插件时的统一入口
 * @param {string} walletId
 * @param {{ onShowInstall?: () => void, onOpening?: () => void }} [callbacks]
 * @returns {() => void} cancel（仅移动端 Deep Link 流程有意义）
 */
export function handleWalletNotInstalled(walletId, callbacks = {}) {
  const { onShowInstall, onOpening } = callbacks;
  if (isMobileBrowser()) {
    onOpening?.();
    return openWalletMobileApp(walletId, { onFallback: onShowInstall });
  }
  openWalletDownload(walletId);
  onShowInstall?.();
  return () => {};
}
